export type LongevityPlanStatus =
  | 'no_plan'
  | 'generating'
  | 'active'
  | 'artifact_failed'
  | 'degraded'
  | 'triage_blocked'
  | 'completed'
  | 'paused';

export type ArtifactStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'degraded';

export type FeedbackType = 'done' | 'not_done' | 'too_hard' | 'not_for_me' | 'replace' | 'evidence';
export type PracticeTaskStatus = 'active' | 'replaced' | 'completed' | 'skipped';

export interface FeedbackCompletionPayload {
  actualMinutes?: number | null;
  actualSets?: number | null;
  actualReps?: number | null;
  rpe?: number | null;
  painScore?: number | null;
  reason?: string | null;
}

export interface PracticePillarSummary {
  key: 'zone2' | 'zone5' | 'strength' | 'stability' | 'recovery' | string;
  label: string;
  value: number;
  unit: string;
  targetLabel: string;
  status: 'on_track' | 'building' | 'missing' | 'observed' | 'ready' | 'watch' | 'downshift' | 'unknown' | string;
  source: string;
}

export interface PracticeRecoverySummary {
  status: 'ready' | 'watch' | 'downshift' | 'unknown' | string;
  label: string;
  summary: string;
  sourceProviders: string[];
  updatedAt?: string | null;
}

export interface LongevityWearableProviderStatus {
  provider: 'apple_health' | 'garmin' | 'manual' | string;
  label: string;
  status: 'synced' | 'not_connected' | 'ready_for_connector' | 'active' | 'available' | string;
  lastSyncedAt?: string | null;
  note: string;
}

export interface LongevityPracticeSummary {
  schemaVersion: 'longevity_practice_summary.v1';
  windowDays: number;
  generatedAt: string;
  pillars: PracticePillarSummary[];
  recovery: PracticeRecoverySummary;
  providerStatuses: LongevityWearableProviderStatus[];
  manualFallbackAvailable: boolean;
  summary: string;
}

export interface AbilityEvidenceItem {
  sourceType: 'practice_summary' | 'workout' | string;
  sourceLabel: string;
  valueLabel: string;
  observedAt?: string | null;
  summary: string;
}

export interface AbilityDomainStatus {
  key: string;
  label: string;
  status: PracticePillarSummary['status'];
  progressLabel: string;
  gapLabel?: string | null;
  evidence: AbilityEvidenceItem[];
}

export interface AbilityLedgerGoal {
  goalCardId: string;
  title: string;
  domains: AbilityDomainStatus[];
}

export interface PracticePlannerBoundary {
  allowedActions: string[];
  blockedActions: string[];
  doctorBoundary: string;
}

export interface AbilityLedgerResponse {
  schemaVersion: 'ability_ledger.v1';
  windowDays: number;
  generatedAt: string;
  goals: AbilityLedgerGoal[];
  plannerBoundary: PracticePlannerBoundary;
  summary: string;
}

export interface WorkoutSessionSummary {
  id: string;
  activityType: string;
  activityName: string;
  startedAt?: string | null;
  endedAt: string;
  durationMinutes: number;
  distanceKilometers?: number | null;
  energyKilocalories?: number | null;
  source: string;
  contributionTags: string[];
}

export interface WorkoutDetailMetric {
  key: string;
  label: string;
  value: number;
  unit: string;
}

export interface WorkoutSessionDetail {
  summary: WorkoutSessionSummary;
  metrics: WorkoutDetailMetric[];
  evidenceBoundary: EvidenceBoundary;
  rawSourceRef: Record<string, unknown>;
}

export interface CycleReviewResponse {
  schemaVersion: 'practice_cycle_review.v1';
  planId: string;
  windowDays: number;
  generatedAt: string;
  completedDays: number;
  workoutCount: number;
  summary: string;
  keepDoing: string[];
  adjustNext: string[];
  dataGaps: string[];
  safetyNotes: string[];
}

export interface WeeklyPracticePrimaryGoal {
  goalCardId: string;
  title: string;
  abilityTags: string[];
  practiceFamilies: string[];
}

export interface WeeklyPracticePillar {
  key: 'zone2' | 'zone5' | 'strength' | 'stability' | 'recovery' | string;
  label: string;
  targetLabel: string;
  status: PracticePillarSummary['status'];
  evidenceSource: string;
  planHint: string;
}

export interface WeeklyPracticeDay {
  dayNumber: number;
  dayId: string;
  status: string;
  focus: string;
  plannedAdjustment: 'maintain' | 'downshift_available' | 'fallback_recommended' | 'hold_intensity' | 'swap_family' | string;
  plannerNote: string;
  taskId: string;
  taskTitle: string;
  doseLabel: string;
  intensity: PracticeTask['difficulty'] | string;
  estimatedMinutes: number;
  whyToday: string;
  fallbackTitle: string;
  fallbackDoseLabel: string;
  safetyStop: string;
  actionBundle?: PracticeActionBundle | null;
  latestFeedbackType?: FeedbackType | null;
}

export interface WeeklyPracticePlannerDecision {
  status: 'maintain' | 'downshift' | 'hold' | 'data_gap' | string;
  label: string;
  rationale: string;
  dataGaps: string[];
  allowedActions: string[];
  blockedActions: string[];
}

export interface WeeklyPracticeAdjustmentRule {
  trigger: string;
  action: string;
  summary: string;
  requiresUserConfirmation: boolean;
}

export interface WeeklyPracticeSafetyBoundary {
  redFlags: string[];
  stopRules: string[];
  doctorBoundary: string;
}

export interface WeeklyPracticePlan {
  schemaVersion: 'weekly_practice_plan.v1' | 'practice_plan.v1';
  planId: string;
  weekStart: string;
  weekEnd: string;
  generatedAt: string;
  title: string;
  summary: string;
  primaryGoal: WeeklyPracticePrimaryGoal;
  pillars: WeeklyPracticePillar[];
  days: WeeklyPracticeDay[];
  plannerDecision: WeeklyPracticePlannerDecision;
  adjustmentRules: WeeklyPracticeAdjustmentRule[];
  safetyBoundary: WeeklyPracticeSafetyBoundary;
  evidenceBoundary: EvidenceBoundary;
}

export interface LongevityGoalCard {
  id: string;
  title: string;
  subtitle: string;
  abilityTags: string[];
  healthSignalAffinity: string[];
  practiceFamilies: string[];
  safetyFlags: string[];
  displayOrder?: number;
  isActive?: boolean;
}

export interface RiskTriageResult {
  schemaVersion: 'risk_triage.v1';
  allowedToGeneratePlan: boolean;
  triageLevel: 'routine' | 'doctor_visit_prep' | 'urgent_care_guidance';
  redFlags: string[];
  userMessage: string;
  nextStep: 'generate_longevity_plan' | 'doctor_visit_prep' | 'urgent_care_guidance';
}

export interface EvidenceBoundary {
  fromUserData: string[];
  generalGuidance: string[];
  missingData: string[];
  doctorBoundary: string;
}

export interface PracticeTask {
  id: string;
  goalCardId: string;
  title: string;
  doseLabel: string;
  difficulty: 'light' | 'medium' | 'challenge';
  estimatedMinutes: number;
  abilityTags: string[];
  alternativeTask: {
    title: string;
    doseLabel: string;
  };
  safetyStop: string;
  status: PracticeTaskStatus;
}

export interface PracticeActionItem {
  title: string;
  doseLabel: string;
  estimatedMinutes: number;
  difficulty: PracticeTask['difficulty'] | string;
}

export interface PracticeObservationCue {
  label: string;
  prompt: string;
  source: string;
}

export interface PracticeDoseOption {
  level: 'foundation' | 'standard' | 'progression' | 'fallback' | string;
  label: string;
  doseLabel: string;
  estimatedMinutes: number;
  requiresUserConfirmation: boolean;
  note: string;
}

export interface PracticeActionBundle {
  schemaVersion: 'practice_action_bundle.v1' | string;
  domain: 'movement' | 'sleep_recovery' | 'nutrition_metabolic' | 'medical_self_management' | 'environment_behavior' | string;
  domainLabel: string;
  primaryAction: PracticeActionItem;
  supportingAction?: PracticeActionItem | null;
  observation: PracticeObservationCue;
  doseOptions: PracticeDoseOption[];
  blockedActions: string[];
  completionMetric: string;
  recommendationReason: string;
}

export interface DailyBrief {
  schemaVersion: 'daily_brief.v1';
  day: number;
  title: string;
  primaryGoalCardId: string;
  goalCardIds: string[];
  oneThesis: string;
  evidenceBoundary: EvidenceBoundary;
  practiceTask: PracticeTask;
  actionBundle?: PracticeActionBundle | null;
  dailyActionLabel: string;
  fallbackActionLabel: string;
  doctorDiscussion: string;
  safetyStop: string;
  artifactStatuses: {
    dailyBrief: ArtifactStatus;
    article: ArtifactStatus;
    practiceTask: ArtifactStatus;
    podcastAudio: ArtifactStatus;
    visualCardSet: ArtifactStatus;
  };
}

export interface LongevityPlan {
  id: string;
  title: string;
  status: LongevityPlanStatus;
  durationDays: number;
  currentDay: number;
  selectedGoalCardIds: string[];
  riskTriage: RiskTriageResult;
  startedAt: string;
  endDate?: string;
  goals?: UserLongevityGoal[];
}

export interface UserLongevityGoal {
  id: string;
  goalCardId: string;
  role: 'primary' | 'vision' | string;
  rank: number;
  personalMotive?: string | null;
  card?: LongevityGoalCard | null;
}

export const longevityGoalCards: LongevityGoalCard[] = [
  {
    id: 'hold_grandchild',
    title: '抱起自己的重孙子',
    subtitle: '保留抱举、核心和下肢支撑能力',
    abilityTags: ['上肢推力', '核心稳定', '下肢力量', '平衡'],
    healthSignalAffinity: ['BMI', '血脂', '睡眠', '肌肉量'],
    practiceFamilies: ['推墙', '坐站', '负重行走'],
    safetyFlags: ['胸闷', '头晕', '明显疼痛'],
  },
  {
    id: 'travel_by_train',
    title: '自己坐高铁去想去的城市',
    subtitle: '保留步行耐力、认知和独立出行',
    abilityTags: ['步行耐力', '心肺', '认知', '自我管理'],
    healthSignalAffinity: ['血压', '血脂', '睡眠', '体力'],
    practiceFamilies: ['步行', '行程整理', '睡眠节律'],
    safetyFlags: ['明显气短', '晕厥', '急性不适'],
  },
  {
    id: 'climb_two_floors',
    title: '爬两层楼去看老朋友',
    subtitle: '保留下肢力量、心肺和日常活动能力',
    abilityTags: ['下肢力量', '心肺', '平衡'],
    healthSignalAffinity: ['血压', 'BMI', '心肺耐力'],
    practiceFamilies: ['坐站', '楼梯准备', '短步行'],
    safetyFlags: ['胸闷', '明显气短', '膝关节疼痛'],
  },
  {
    id: 'stand_from_floor',
    title: '蹲下来捡东西，还能稳稳站起来',
    subtitle: '保留髋膝活动度、核心和防跌倒能力',
    abilityTags: ['髋膝活动度', '核心', '平衡'],
    healthSignalAffinity: ['骨密度', '肌肉量', '关节疼痛'],
    practiceFamilies: ['坐站', '平衡', '活动度'],
    safetyFlags: ['明显疼痛', '头晕', '跌倒风险'],
  },
  {
    id: 'carry_groceries',
    title: '提一袋菜走回家',
    subtitle: '保留握力、负重行走和稳定步态',
    abilityTags: ['握力', '负重行走', '心肺'],
    healthSignalAffinity: ['肌肉量', 'BMI', '血脂'],
    practiceFamilies: ['握力', '负重行走', '步态'],
    safetyFlags: ['胸闷', '手腕疼痛', '明显气短'],
  },
  {
    id: 'open_bottle',
    title: '拧开瓶盖、拿稳杯子',
    subtitle: '保留手部力量和精细动作',
    abilityTags: ['握力', '手部功能', '协调'],
    healthSignalAffinity: ['肌肉量', '炎症线索', '关节疼痛'],
    practiceFamilies: ['握力', '手指活动', '轻负重'],
    safetyFlags: ['明显疼痛', '麻木加重'],
  },
  {
    id: 'night_walk_safe',
    title: '晚上起夜稳稳走到洗手间',
    subtitle: '保留夜间平衡、睡眠和防跌倒能力',
    abilityTags: ['平衡', '睡眠', '防跌倒'],
    healthSignalAffinity: ['睡眠', '血压', '夜尿线索'],
    practiceFamilies: ['平衡', '睡眠节律', '环境整理'],
    safetyFlags: ['晕厥', '跌倒', '意识模糊'],
  },
  {
    id: 'talk_to_doctor',
    title: '清楚地和医生沟通自己的报告',
    subtitle: '保留健康素养、表达和自我管理',
    abilityTags: ['健康素养', '认知', '自我管理'],
    healthSignalAffinity: ['体检报告', '用药史', '复查计划'],
    practiceFamilies: ['报告整理', '问题清单', '复查准备'],
    safetyFlags: ['急性症状', '药物变更', '诊断不确定'],
  },
];

export const mockLongevityPlan: LongevityPlan = {
  id: 'longevity-plan-demo',
  title: '28 天 Attia 式能力训练',
  status: 'active',
  durationDays: 28,
  currentDay: 1,
  selectedGoalCardIds: ['hold_grandchild', 'climb_two_floors', 'talk_to_doctor'],
  startedAt: '2026-05-06',
  riskTriage: {
    schemaVersion: 'risk_triage.v1',
    allowedToGeneratePlan: true,
    triageLevel: 'routine',
    redFlags: [],
    userMessage: '目前没有发现阻止创建普通百岁实践计划的安全风险线索。',
    nextStep: 'generate_longevity_plan',
  },
};

export const mockDailyBrief: DailyBrief = {
  schemaVersion: 'daily_brief.v1',
  day: 1,
  title: '把“抱起自己的重孙子”拆成今天要保留的能力',
  primaryGoalCardId: 'hold_grandchild',
  goalCardIds: mockLongevityPlan.selectedGoalCardIds,
  oneThesis: '长期行动能力不是由单个指标决定，而是由肌力、代谢、睡眠和日常习惯一起支撑。',
  evidenceBoundary: {
    fromUserData: ['目前资料提示运动和体重管理值得持续关注'],
    generalGuidance: ['轻量抗阻动作通常可作为恢复肌力的低摩擦起点'],
    missingData: ['缺少近 30 天运动、睡眠和肌肉量数据'],
    doctorBoundary: '具体风险分层、疼痛原因和运动禁忌需要带着报告与医生确认。',
  },
  practiceTask: {
    id: 'task-day1-wall-pushup',
    goalCardId: 'hold_grandchild',
    title: '靠墙俯卧撑',
    doseLabel: '6 次 x 2 组',
    difficulty: 'light',
    estimatedMinutes: 6,
    abilityTags: ['上肢推力', '核心稳定'],
    alternativeTask: {
      title: '坐姿推墙',
      doseLabel: '5 次',
    },
    safetyStop: '如果出现胸闷、头晕、明显疼痛，停止动作并咨询医生。',
    status: 'active',
  },
  dailyActionLabel: '靠墙俯卧撑 6 次 x 2 组',
  fallbackActionLabel: '坐姿推墙 5 次',
  doctorDiscussion: '下次复查时可询问血脂、体重管理目标和适合自己的运动边界。',
  safetyStop: '如果出现胸闷、头晕、明显疼痛，停止动作并咨询医生。',
  artifactStatuses: {
    dailyBrief: 'completed',
    article: 'completed',
    practiceTask: 'completed',
    podcastAudio: 'degraded',
    visualCardSet: 'completed',
  },
};

export const feedbackOptions: Array<{ type: FeedbackType; label: string }> = [
  { type: 'done', label: '做了' },
  { type: 'not_done', label: '没做' },
  { type: 'too_hard', label: '太难' },
  { type: 'not_for_me', label: '不适合' },
  { type: 'replace', label: '想换一个' },
];

export function getGoalCardById(id: string) {
  return longevityGoalCards.find((card) => card.id === id);
}

export function getPlanGoalCards(plan: LongevityPlan) {
  const cardsFromPlan = plan.goals
    ?.map((goal) => goal.card)
    .filter((card): card is LongevityGoalCard => Boolean(card));
  if (cardsFromPlan?.length) return cardsFromPlan;

  return plan.selectedGoalCardIds
    .map((id) => getGoalCardById(id))
    .filter((card): card is LongevityGoalCard => Boolean(card));
}
